import { Conversation, ConversationContent, ConversationScrollButton } from '../../ai-elements/conversation.js';
import React__default, { memo, useMemo, useCallback } from 'react';
import { useConversationScrollDetection } from '../hooks/useConversationScrollDetection.js';
import ConciergeWelcome from './ConciergeWelcome.js';
import { ConversationSkeleton } from './ConversationSkeleton.js';
import { MessageListV2 } from './MessageListV2.js';
import { PromptInputConcierge } from './PromptInputConcierge.js';

// Separate input component that can re-render without affecting conversation area
const ConciergeInputArea = ({ searchInputValue, setSearchInputValue, handleSearch, isActivelyStreaming, conversationHistoryLength, onTrackAccessPoint, }) => {
    const placeholder = useMemo(() => (conversationHistoryLength > 0 ? 'Ask a follow up question' : 'How can I help you today?'), [conversationHistoryLength]);
    // Wrap handleSearch to track the access point
    const handleSearchWithTracking = useCallback((text, imageData, imageMimeType) => {
        // Track search_bar access point (non-blocking)
        onTrackAccessPoint === null || onTrackAccessPoint === void 0 ? void 0 : onTrackAccessPoint('search_bar', text);
        handleSearch(text, imageData, imageMimeType);
    }, [handleSearch, onTrackAccessPoint]);
    return (React__default.createElement("div", { className: "tw:flex tw:shrink-0 tw:flex-col tw:gap-4 tw:px-4 tw:pt-4 md:tw:px-0", style: { paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' } },
        React__default.createElement(PromptInputConcierge, { placeholder: placeholder, value: searchInputValue, onChange: setSearchInputValue, onSubmit: handleSearchWithTracking, isStreaming: isActivelyStreaming })));
};
const ConciergeChatAreaV2Component = ({ appName, brandId, conversationId, isEmbedded = false, apiBaseUrl, pageContext, conversationHistory, productMetadataMap, streamingProductGroups, previewSearchQueries, previewQueryProducts, followUpQuestions, streamingFeaturedProduct, streamingStoreLocator, isActivelyStreaming, streamingAIResponse, currentStatus, isWaitingForFollowUpQueries, isLoadingConversationQuery, handleSearch, onUpdateFeedback, onVariantSelect, onTrackAccessPoint, searchInputValue, setSearchInputValue, }) => {
    // Derived state
    const showWelcome = conversationHistory.length === 0 && !isLoadingConversationQuery;
    // Show typing indicator only when actively streaming but no search queries/products AND no text content yet
    const showTypingIndicator = isActivelyStreaming &&
        !streamingAIResponse &&
        previewSearchQueries.length === 0 &&
        previewQueryProducts.length === 0;
    // Scroll detection for auto-scrolling to bottom
    // Memoize the hook call to prevent re-renders when searchInputValue changes
    const scrollDetectionParams = useMemo(() => ({
        followUpQuestions,
        isActivelyStreaming,
        conversationHistoryLength: conversationHistory.length,
        showWelcome,
        searchInputValue: '', // Not actually used in the hook, but required by interface
    }), [followUpQuestions, isActivelyStreaming, conversationHistory.length, showWelcome]);
    const { bottomRef } = useConversationScrollDetection(scrollDetectionParams);
    // Memoize handleSearch callback to prevent welcome component re-renders
    const stableHandleSearch = useMemo(() => handleSearch, [handleSearch]);
    // Create wrapped handlers that track access points
    const handleWelcomeSearch = useCallback((searchTerm) => {
        // Track quick_prompts access point (non-blocking)
        onTrackAccessPoint === null || onTrackAccessPoint === void 0 ? void 0 : onTrackAccessPoint('quick_prompts', searchTerm);
        stableHandleSearch(searchTerm);
    }, [stableHandleSearch, onTrackAccessPoint]);
    const handleFollowUpSearch = useCallback((searchTerm) => {
        // Track follow_up_question access point (non-blocking)
        onTrackAccessPoint === null || onTrackAccessPoint === void 0 ? void 0 : onTrackAccessPoint('follow_up_question', searchTerm);
        stableHandleSearch(searchTerm);
    }, [stableHandleSearch, onTrackAccessPoint]);
    // Memoize welcome component props to prevent re-renders
    const welcomeProps = useMemo(() => ({
        onSearch: handleWelcomeSearch,
        brandId: brandId ? parseInt(brandId) : undefined,
        isEmbedded,
        apiBaseUrl,
        pageContext,
    }), [handleWelcomeSearch, brandId, isEmbedded, apiBaseUrl, pageContext]);
    // Memoize the conversation content to prevent re-renders when searchInputValue changes
    const conversationContent = useMemo(() => {
        if (isLoadingConversationQuery) {
            return React__default.createElement(ConversationSkeleton, null);
        }
        if (showWelcome) {
            return React__default.createElement(ConciergeWelcome, Object.assign({}, welcomeProps));
        }
        return (React__default.createElement(MessageListV2, { conversationId: conversationId, messages: conversationHistory, showTypingIndicator: showTypingIndicator, streamingAIResponse: streamingAIResponse, productMetadataMap: productMetadataMap, streamingProductGroups: streamingProductGroups, currentStatus: currentStatus, previewSearchQueries: previewSearchQueries, previewQueryProducts: previewQueryProducts, isWaitingForFollowUpQueries: isWaitingForFollowUpQueries, isActivelyStreaming: isActivelyStreaming, streamingFeaturedProduct: streamingFeaturedProduct, streamingStoreLocator: streamingStoreLocator, followUpQuestions: followUpQuestions, onSearch: handleFollowUpSearch, onUpdateFeedback: onUpdateFeedback, onVariantSelect: onVariantSelect, onSuggestionClick: setSearchInputValue, bottomRef: bottomRef, apiBaseUrl: apiBaseUrl }));
    }, [
        isLoadingConversationQuery,
        showWelcome,
        welcomeProps,
        conversationId,
        conversationHistory,
        showTypingIndicator,
        streamingAIResponse,
        productMetadataMap,
        streamingProductGroups,
        currentStatus,
        previewSearchQueries,
        previewQueryProducts,
        isWaitingForFollowUpQueries,
        isActivelyStreaming,
        streamingFeaturedProduct,
        streamingStoreLocator,
        followUpQuestions,
        handleFollowUpSearch,
        onUpdateFeedback,
        onVariantSelect,
        setSearchInputValue,
        bottomRef,
        apiBaseUrl,
    ]);
    return (React__default.createElement("div", { className: "tw:relative tw:flex tw:flex-1 tw:flex-col", style: {
            minHeight: 0,
            maxHeight: '100%',
            overflow: 'hidden',
        } },
        React__default.createElement("div", { style: {
                flex: 1,
                overflow: showWelcome ? 'auto' : 'auto',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                maxHeight: '100%',
            } }, showWelcome ? (React__default.createElement("div", { style: {
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            } }, conversationContent)) : (React__default.createElement(Conversation, null,
            React__default.createElement(ConversationContent, { className: "tw:flex tw:items-center tw:justify-center" }, conversationContent),
            React__default.createElement(ConversationScrollButton, null)))),
        React__default.createElement(ConciergeInputArea, { searchInputValue: searchInputValue, setSearchInputValue: setSearchInputValue, handleSearch: handleSearch, isActivelyStreaming: isActivelyStreaming, conversationHistoryLength: conversationHistory.length, onTrackAccessPoint: onTrackAccessPoint })));
};
// Memoize the chat area - input is separated so it can re-render without affecting conversation
const ConciergeChatAreaV2 = memo(ConciergeChatAreaV2Component);

export { ConciergeChatAreaV2 };
//# sourceMappingURL=ConciergeChatAreaV2.js.map
